import { Stack, alpha, styled } from '@mui/material';
import { AiLoading } from 'src/components/ai/AiLoading';
import {
  avatarSizeBorder,
  avatarSizes,
} from 'src/components/base/CPAvatar/avatarSizes';
import { CustomAvatar } from 'src/components/custom-avatar';
import { SMALL_MOBILE_BREAKPOINT } from 'src/config-global';

export const MemberCard = styled(Stack)(({ theme }) => ({
  position: 'relative',
  flexDirection: 'row',
  alignItems: 'center',

  border: `2px solid transparent`,
  width: '100%',
  height: '100%',

  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  borderColor: theme.palette.divider,

  transition: theme.transitions.create(['all'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.complex,
  }),

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'column',
    placeItems: 'center',
    justifyContent: 'center',
    paddingBottom: 46,
    padding: theme.spacing(3),

    '&.isFromNav': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      minHeight: 150,
      maxHeight: 150,
      border: 'none',
      borderTop: `1px solid ${theme.palette.divider}`,
      '&:hover': {
        '& .go-back-button': {
          opacity: 1,
          color: theme.palette.common.white,
          zIndex: 3,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.customShadows.card2,
          zIndex: 2,
          pointerEvents: 'none',
        },
      },
    },

    '&.hasVideo.theater': {
      width: '100%',
    },
    '&.noTheater': {
      justifyContent: 'center',
      cursor: 'nwse-resize',
      '& .name': {
        ...theme.typography.caption,
        fontWeight: theme.typography.fontWeightBold,
      },
      '& .video-action-buttons': {
        display: 'none',
      },
    },
  },

  '&:not(.connected)': {
    justifyContent: 'center',
    '& .name-container, & .top-right-icons, & .video-action-button': {
      display: 'none',
    },
  },

  '&.isSpeaking:not(.isFromNav)': {
    borderColor: theme.palette.success.main,
    '& .name-container': {
      backgroundColor: theme.palette.success.main,
      '& .video-icon': {
        color: theme.palette.common.black,
      },
    },
  },

  '&.deaf': {
    opacity: 0.8,
  },

  '&.hasVideo.connected': {
    flexDirection: 'column',
    placeItems: 'center',
    justifyContent: 'center',
    padding: 0,
    '&:not(.isFromNav)': {
      backgroundColor: `${theme.palette.common.black} !important`,
    },
  },

  '&.raisedHand': {
    borderColor: theme.palette.warning.main,
  },

  '&:not(.muted, .deaf)': {
    backgroundColor: theme.palette.background.paper,
  },

  '&.isP2P': {
    '&.aloneMember': {
      alignSelf: 'center',
      maxWidth: '25%',
    },
    aspectRatio: 16 / 9,
  },
}));

export const MemberInfo = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: 1,
  width: '100%',
  padding: theme.spacing(1),
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
  gap: theme.spacing(1),
  zIndex: 1,

  '&:not(.connected)': {
    display: 'none',
  },

  '&.hasVideo': {
    position: 'absolute',
    bottom: 0,
    left: 0,
    background: `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0)} 0%, ${alpha(
      theme.palette.common.black,
      0.7,
    )} 100%)`,
  },
  [theme.breakpoints.up('sm')]: {
    position: 'absolute',
    bottom: 0,
    left: 0,
  },
}));

export const MemberNameContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',

  backgroundColor: alpha(theme.palette.ai[100], 0.48),
  color: theme.palette.common.white,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5, 1),
  gap: theme.spacing(0.5),

  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}));

export const MemberName = styled('span')(({ theme }) => ({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  ...theme.typography.subtitle2,
}));

export const TopRightIconsStyled = styled(Stack)(({ theme }) => ({
  position: 'absolute',
  flexDirection: 'row',
  alignItems: 'center',
  top: theme.spacing(0.5),
  right: theme.spacing(1),
  gap: theme.spacing(1),
  zIndex: 1,
}));

export const GoBackNavButton = styled(Stack)(({ theme }) => ({
  position: 'absolute',
  flexDirection: 'row',
  alignItems: 'center',
  top: theme.spacing(1),
  left: theme.spacing(1),
  gap: theme.spacing(1),
  zIndex: 1,
  cursor: 'pointer',
  transition: 'opacity 0.2s ease-in-out',
  opacity: 0,
}));

export const TakingNotesStyled = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  borderRadius: theme.spacing(1),
  backgroundColor: alpha(theme.palette.ai[100], 0.48),
  padding: theme.spacing(0.5, 1),
  gap: theme.spacing(0.5),
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
  },
}));

// VoiceChannelMemberContext styles

export const StyledAiLoading = styled(AiLoading)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  '& .stars': {
    transform: 'scale(0.8)',
  },
  [theme.breakpoints.up(SMALL_MOBILE_BREAKPOINT)]: {
    width: 92,
    height: 92,
  },
}));

export const CustomCallingAvatar = styled(CustomAvatar)(({ theme }) => ({
  '&.isSmall': {
    ...avatarSizes.medium,
  },
  '&:not(.isSmall)': {
    ...avatarSizes.xLarge,
  },
  clipPath: 'none !important',
  border: 'none',
  zIndex: 1,
  '&.ringing': {
    border: `${avatarSizeBorder.xLarge}px solid ${theme.palette.success.main}`,
  },
  '&.calling': {
    border: `${avatarSizeBorder.xLarge}px solid ${theme.palette.text.secondary}`,
  },
  '&.declined, &.not-answered': {
    border: `${avatarSizeBorder.xLarge}px solid ${theme.palette.error.main}`,
  },
}));
